/**
 * Enhanced Notification Service
 * Provides real-time notification management with WebSocket integration
 */

import realApiService from './realApiService.js';
import sessionService from './sessionService.js';

class EnhancedNotificationService {
  constructor() {
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners = new Set();
    this.isInitialized = false;
    this.pollingInterval = null;
    this.pollingIntervalMs = 30000; // 30 seconds
    this.websocket = null;
  }

  /**
   * Initialize the notification service
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('📢 Enhanced Notification Service already initialized');
      return;
    }

    console.log('📢 Initializing Enhanced Notification Service...');

    // Check if user is authenticated before initializing
    if (!sessionService.isAuthenticated()) {
      console.log('ℹ️ User not authenticated, skipping notification service initialization');
      // Do not mark as initialized to allow retry when user logs in
      return;
    }

    try {
      // Try to establish WebSocket connection
      await this.initializeWebSocket();

      // Start periodic polling as fallback
      this.startPolling();

      // Load initial notifications
      await this.loadNotifications();

      this.isInitialized = true;
      console.log('✅ Enhanced Notification Service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Enhanced Notification Service:', error);
      // Fallback to polling only
      this.startPolling();
      this.isInitialized = true;
    }
  }

  /**
   * Initialize WebSocket connection for real-time notifications
   */
  async initializeWebSocket() {
    try {
      // Check if WebSocket service is available
      if (window.websocketService) {
        console.log('🔌 Setting up WebSocket listeners for notifications...');
        
        // Listen for notification events
        window.websocketService.on('notification_created', this.handleNotificationCreated.bind(this));
        window.websocketService.on('notification_updated', this.handleNotificationUpdated.bind(this));
        window.websocketService.on('notification_deleted', this.handleNotificationDeleted.bind(this));
        window.websocketService.on('notifications_marked_read', this.handleNotificationsMarkedRead.bind(this));
        
        console.log('✅ WebSocket listeners set up for notifications');
      } else {
        console.log('⚠️ WebSocket service not available for notifications');
      }
    } catch (error) {
      console.warn('⚠️ Failed to set up WebSocket for notifications:', error);
    }
  }

  /**
   * Start periodic polling for notifications
   */
  startPolling() {
    if (this.pollingInterval) {
      console.log('📢 Notification polling already running');
      return;
    }

    console.log(`📢 Starting notification polling every ${this.pollingIntervalMs / 1000} seconds`);

    this.pollingInterval = setInterval(() => {
      this.loadNotifications();
    }, this.pollingIntervalMs);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollingInterval) {
      console.log('⏹️ Stopping notification polling');
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Load notifications from API
   */
  async loadNotifications() {
    try {
      console.log('📢 Loading notifications...');

      // Check if user is authenticated first
      if (!sessionService.isAuthenticated()) {
        console.log('ℹ️ User not authenticated, skipping notification load');
        this.notifications = [];
        this.unreadCount = 0;
        this.notifyListeners('notifications_loaded', this.notifications);
        return;
      }
      

      
      let response;
      try {
        // Try to get user-specific notifications first which supports filtering and is more robust
        response = await realApiService.notifications.getUserNotifications({
          limit: 50,
          include_read: true
        });
        
        // Handle result format
        if (response && response.notifications) {
          response = response.notifications;
        } else if (response && response.data) {
          response = response.data;
        }
      } catch (inAppError) {
        console.warn('In-app notifications fetch failed, trying generic endpoint:', inAppError);
        // Fallback to generic endpoint
        response = await realApiService.notifications.getAll();
      }

      if (response && Array.isArray(response)) {
        const rawData = response;
        
        // Parse metadata if it's a string
        this.notifications = rawData.map(notification => {
          let metadata = notification.notification_metadata;
          if (typeof metadata === 'string') {
            try {
              metadata = JSON.parse(metadata);
            } catch (e) {
              console.warn('Failed to parse notification metadata:', e);
            }
          }
          return {
            ...notification,
            notification_metadata: metadata
          };
        });

        this.updateUnreadCount();
        this.notifyListeners('notifications_loaded', this.notifications);
        console.log(`✅ Loaded ${this.notifications.length} notifications`);
      } else {
        console.warn('⚠️ Invalid notifications response format:', response);
        this.notifications = [];
        this.unreadCount = 0;
        this.notifyListeners('notifications_loaded', []);
      }
    } catch (error) {
      console.error('❌ Failed to load notifications:', error);

      // If it's an authentication error, clear notifications
      if (error.message?.includes('Authentication') ||
          error.message?.includes('401') ||
          error.message?.includes('Method Not Allowed') ||
          error.status === 401 ||
          error.status === 405) {
        console.log('🔐 Authentication error detected, clearing notifications');
        this.notifications = [];
        this.unreadCount = 0;
        this.notifyListeners('notifications_loaded', this.notifications);
      } else {
        this.notifyListeners('notifications_error', error);
      }
    }
  }

  /**
   * Update unread count
   */
  updateUnreadCount() {
    const unread = this.notifications.filter(n => !n.read && !n.read_at && !n.isRead).length;
    if (unread !== this.unreadCount) {
      this.unreadCount = unread;
      this.notifyListeners('unread_count_changed', this.unreadCount);
    }
  }

  /**
   * Get all notifications
   */
  getNotifications() {
    return this.notifications;
  }

  /**
   * Get unread count
   */
  getUnreadCount() {
    return this.unreadCount;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      console.log('📢 Marking notification as read:', notificationId);
      
      await realApiService.notifications.markAsRead(notificationId);
      
      // Update local state
      this.notifications = this.notifications.map(n => 
        n.id === notificationId 
          ? { ...n, read: true, read_at: new Date().toISOString(), isRead: true }
          : n
      );
      
      this.updateUnreadCount();
      this.notifyListeners('notification_marked_read', notificationId);
      
      console.log('✅ Notification marked as read');
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      console.log('📢 Marking all notifications as read...');
      
      await realApiService.notifications.markAllAsRead();
      
      // Update local state
      this.notifications = this.notifications.map(n => ({
        ...n,
        read: true,
        read_at: new Date().toISOString(),
        isRead: true
      }));
      
      this.unreadCount = 0;
      this.notifyListeners('all_notifications_marked_read', this.notifications);
      this.notifyListeners('unread_count_changed', 0);
      
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('❌ Failed to mark all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      console.log('📢 Deleting notification:', notificationId);
      
      await realApiService.notifications.delete(notificationId);
      
      // Update local state
      this.notifications = this.notifications.filter(n => n.id !== notificationId);
      this.updateUnreadCount();
      this.notifyListeners('notification_deleted', notificationId);
      
      console.log('✅ Notification deleted');
    } catch (error) {
      console.error('❌ Failed to delete notification:', error);
      throw error;
    }
  }

  /**
   * Handle WebSocket notification created event
   */
  handleNotificationCreated(data) {
    console.log('🔌 New notification received via WebSocket:', data);
    
    // Add to local notifications
    this.notifications.unshift(data);
    this.updateUnreadCount();
    this.notifyListeners('notification_created', data);
  }

  /**
   * Handle WebSocket notification updated event
   */
  handleNotificationUpdated(data) {
    console.log('🔌 Notification updated via WebSocket:', data);
    
    // Update local notification
    this.notifications = this.notifications.map(n => 
      n.id === data.id ? { ...n, ...data } : n
    );
    
    this.updateUnreadCount();
    this.notifyListeners('notification_updated', data);
  }

  /**
   * Handle WebSocket notification deleted event
   */
  handleNotificationDeleted(data) {
    console.log('🔌 Notification deleted via WebSocket:', data);
    
    // Remove from local notifications
    this.notifications = this.notifications.filter(n => n.id !== data.id);
    this.updateUnreadCount();
    this.notifyListeners('notification_deleted', data.id);
  }

  /**
   * Handle WebSocket notifications marked read event
   */
  handleNotificationsMarkedRead(data) {
    console.log('🔌 Notifications marked read via WebSocket:', data);
    
    if (data.all) {
      // Mark all as read
      this.notifications = this.notifications.map(n => ({
        ...n,
        read: true,
        read_at: new Date().toISOString(),
        isRead: true
      }));
      this.unreadCount = 0;
      this.notifyListeners('all_notifications_marked_read', this.notifications);
    } else if (data.ids && Array.isArray(data.ids)) {
      // Mark specific notifications as read
      this.notifications = this.notifications.map(n => 
        data.ids.includes(n.id)
          ? { ...n, read: true, read_at: new Date().toISOString(), isRead: true }
          : n
      );
      this.updateUnreadCount();
      this.notifyListeners('notifications_marked_read', data.ids);
    }
    
    this.notifyListeners('unread_count_changed', this.unreadCount);
  }

  /**
   * Add listener for notification events
   */
  addListener(callback) {
    console.log('👂 Adding notification listener');
    this.listeners.add(callback);
    
    return () => {
      console.log('🗑️ Removing notification listener');
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Cleanup service
   */
  cleanup() {
    console.log('🧹 Cleaning up Enhanced Notification Service...');
    
    this.stopPolling();
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners.clear();
    this.isInitialized = false;
    
    console.log('✅ Enhanced Notification Service cleaned up');
  }
}

// Create singleton instance
const enhancedNotificationService = new EnhancedNotificationService();

export default enhancedNotificationService;
